import { atom } from 'jotai/vanilla';
import { selectAtom } from 'jotai/vanilla/utils';
import type { AuthStatus } from './authStatus';
import type { RegistrationDraft } from './registrationDraft';

/**
 * 認証状態を確定できなかった理由。
 *
 * - 'network': 通信起因でカスタムクレイムまたは認証セッションを取得できなかった。
 *   Firebase 側にはサインイン済みのユーザーが残っているため、未ログインとは区別して
 *   「表示できない」旨を案内する。
 */
type AuthError = 'network';

/**
 * Jotai で管理する認証状態。アプリ全体の認証状態のソース・オブ・トゥルース。
 *
 * 認証状態マシン(AuthStateLoader 内の Firebase Auth 購読)が本 atom を同期的に
 * 書き込み、各消費側は本 atom(派生 {@link authStatusAtom})を読み取る。
 *
 * {@link AuthStatus} に加えて、認証状態の初期化中(loading)状態も保持する。
 */
type AuthState = {
  loading: boolean;
  authStatus: AuthStatus;
  // pendingRegistration 状態が保持する登録前の一時情報。それ以外の状態では null。
  registrationDraft: RegistrationDraft | null;
  // 認証状態を確定できなかった場合の理由。確定できていない場合は null。
  authError: AuthError | null;
};

const initialAuthState: AuthState = {
  loading: true,
  authStatus: {
    status: 'unauthenticated',
    currentUserId: null,
    currentSessionId: null,
    userType: null,
    signInAt: null,
  },
  registrationDraft: null,
  authError: null,
};

export const authStateAtom = atom<AuthState>(initialAuthState);

/**
 * 認証状態のうち {@link AuthStatus} だけを購読する読み取り用の派生 atom。
 * loading の変化では発火しないため、認証情報を参照する消費側の無駄な再描画を防ぐ。
 */
export const authStatusAtom = selectAtom(authStateAtom, (state) => state.authStatus);
